/**
 * Supporting activity/factor evidence copied from the same fetchGHGInventoryData
 * run that produces emissions. This module does not calculate emissions.
 */

export interface InventoryYearSeries {
  years: number[];
  values: Array<number | null>;
  statuses?: Array<string | null>;
  sources?: Array<string | null>;
  unit: string;
}

export interface GhgInventoryEvidence {
  years: number[];
  ldvVmt: InventoryYearSeries;
  hdvVmt: InventoryYearSeries;
  elecResKwh: InventoryYearSeries;
  elecComKwh: InventoryYearSeries;
  ngResTherms: InventoryYearSeries;
  ngComTherms: InventoryYearSeries;
  wasteTons: InventoryYearSeries;
  wasteEmissionFactorMtPerTon: InventoryYearSeries;
  electricityCarbonIntensityGPerKwh: InventoryYearSeries;
  ngEmissionFactorMtPerTherm: number | null;
  ngEmissionFactorNote: string;
  replicaMethodVersion: string | null;
  replicaAttribution: string;
  hdvClass: string;
  electricityProviderByYear?: Array<string | null>;
  /** True when the software replaced that year's HDV emissions with the previous year (>50% YoY rule). */
  hdvSmoothingApplied?: Array<boolean | null>;
  /** Whether displayed HDV VMT is the emissions identity input or contextual fleet activity. */
  hdvActivityRole?: Array<'calculation_input' | 'contextual' | null>;
}

export function emptyYearSeries(years: number[], unit: string): InventoryYearSeries {
  return { years, values: years.map(() => null), statuses: years.map(() => null), sources: years.map(() => null), unit };
}

export function seriesValueAt(series: InventoryYearSeries | undefined, year: number): number | null {
  if (!series) return null;
  const idx = series.years.indexOf(year);
  if (idx < 0) return null;
  const n = series.values[idx];
  if (n == null || !Number.isFinite(Number(n))) return null;
  return Number(n);
}

export function seriesStatusAt(series: InventoryYearSeries | undefined, year: number): string | null {
  if (!series?.statuses) return null;
  const idx = series.years.indexOf(year);
  return idx < 0 ? null : series.statuses[idx] ?? null;
}
